import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { toast } from "react-toastify";
import { PhoneNumberUtil } from "google-libphonenumber";

import { AdminService, CRMService } from "../../services";
import { leadValidationRules } from "../../pages/crm/validation";
import { ActionButton, FormDialog, InputField, PhoneInput, SelectField } from "../v3";

const phoneUtil = PhoneNumberUtil.getInstance();

const isPhoneValid = (phone) => {
  try {
    return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone));
  } catch {
    return false;
  }
};

export const AddEditLead = ({ open, onClose, leadId, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState(false);
  const [leadDetails, setLeadDetails] = useState({
    contactName: "",
    contactPhone: "",
    statusId: "",
    priorityId: undefined,
    assigneeIds: [],
  });
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  const [selectedPriority, setSelectedPriority] = useState(null);

  const isValid = isPhoneValid(leadDetails.contactPhone);
  const showError = (touched && (!leadDetails.contactPhone || !isValid)) || !!errors.contactPhone;

  useEffect(() => {
    if (open && leadId) {
      fetchLeadDetails(leadId);
    } else if (open && !leadId) {
      handleClearForm();
    }
  }, [open, leadId]);

  const fetchLeadDetails = async (id) => {
    try {
      const response = await CRMService.getLead(id);
      setLeadDetails({
        contactName: response?.data?.contactName || "",
        contactPhone: response.data.contactPhone || "",
      });
    } catch (error) {
      toast("Failed to fetch lead details", { type: "error" });
      console.error("fetchData error:", error);
    }
  };

  const handleStatusChange = (selected) => {
    setSelectedStatus(selected);
    setLeadDetails((prev) => ({ ...prev, statusId: selected?.id || null }));
  };

  const handleAssigneeChange = (selected) => {
    const selectedList = Array.isArray(selected) ? selected : [];
    setSelectedAssignees(selectedList);
    setLeadDetails((prev) => ({
      ...prev,
      assigneeIds: selectedList.map((assignee) => assignee?.id).filter((id) => id != null),
    }));
  };

  const handlePriorityChange = (selected) => {
    setSelectedPriority(selected);
    setLeadDetails((prev) => ({ ...prev, priorityId: selected?.id || null }));
  };

  const handleClearForm = () => {
    setLeadDetails({
      contactName: "",
      contactPhone: "",
      statusId: "",
      priorityId: undefined,
      assigneeIds: [],
    });
    setSelectedStatus(null);
    setSelectedAssignees([]);
    setSelectedPriority(null);
  };

  const updateDetailsState = (event) => {
    const { name, value } = event.target;
    setLeadDetails((prev) => ({ ...prev, [name]: value }));
  };

  const handleCloseModal = () => {
    handleClearForm();
    setErrors({});
    onClose();
  };

  const handleSaveLead = async () => {
    setErrors({});

    const validationErrors = leadValidationRules(leadDetails, leadId);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const payload = { ...leadDetails, name: leadDetails.contactName };
      if (leadId) await CRMService.updateLead(leadId, payload);
      else await CRMService.createLead(payload);
      toast.success(`Lead ${leadId ? "updated" : "added"} successfully`);

      if (onSuccess) onSuccess();
      handleCloseModal();
    } catch (error) {
      let errorMessage = `Error ${leadId ? "updating" : "adding"} lead`;
      const response = error?.response?.data;

      if (Array.isArray(response?.message)) {
        errorMessage = response.message.join(", ");
      } else if (typeof response?.message === "string") {
        errorMessage = response.message;
      }

      toast(errorMessage, { type: "error" });
      console.error("handleSaveLead error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormDialog
      open={open}
      title={leadId ? "Edit Lead" : "Add Lead"}
      actions={
        <>
          <ActionButton buttonText="Cancel" onButtonClick={handleCloseModal} variant="outlined" />
          <ActionButton buttonText="Save" onButtonClick={handleSaveLead} loading={loading} />
        </>
      }
    >
      <Box mt={1}>
        <Box className="fl gap-2">
          <InputField
            required
            label="Contact Name"
            name="contactName"
            value={leadDetails.contactName}
            onChange={updateDetailsState}
            error={!!errors.contactName}
            helperText={errors.contactName}
          />
          <Box className="fl gap-2">
            <PhoneInput
              label="Mobile"
              name="contactPhone"
              defaultCountry="in"
              value={leadDetails.contactPhone}
              size="small"
              fullWidth
              required
              onChange={(e164) => setLeadDetails((prev) => ({ ...prev, contactPhone: e164 }))}
              onBlur={() => setTouched(true)}
              error={showError}
              helperText={
                errors.contactPhone ||
                (showError ? (leadDetails.contactPhone ? "Enter a valid phone number" : "Mobile number is required") : " ")
              }
            />
          </Box>
        </Box>
        {!leadId && (
          <>
            <Box className="fl gap-2">
              <SelectField
                label="Status"
                value={selectedStatus}
                getData={CRMService.getLeadStatuses}
                required
                error={!!errors.statusId}
                helperText={errors.statusId}
                onChange={handleStatusChange}
                style={{ width: "100%" }}
              />
              <SelectField
                label="Assignees"
                value={selectedAssignees}
                multiSelect
                limitTags={1}
                getData={AdminService.getUserList}
                getDataStaticFilters={{ type: "USER", active: true }}
                optionValueFields={["firstName", "lastName"]}
                required
                error={!!errors.assigneeIds}
                helperText={errors.assigneeIds}
                onChange={handleAssigneeChange}
                style={{
                  width: "100%",
                  "& .MuiAutocomplete-inputRoot": { flexWrap: "nowrap" },
                  "& .MuiAutocomplete-tag": { maxWidth: "100%" },
                  "& .MuiChip-label": { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
                  "& .MuiAutocomplete-input": { minWidth: 0 },
                }}
                listboxProps={{ maxHeight: 240 }}
              />
            </Box>
            <Box className="fl gap-2">
              <SelectField
                label="Priority"
                value={selectedPriority}
                getData={CRMService.getLeadPriorities}
                getDataStaticFilters={{ includeGlobal: true }}
                required
                error={!!errors.priorityId}
                helperText={errors.priorityId}
                onChange={handlePriorityChange}
                style={{ width: "100%" }}
              />
            </Box>
          </>
        )}
      </Box>
    </FormDialog>
  );
};
